import { useEffect, useRef } from "react";
import { useAuth } from "zudoku/hooks";

// Configuration from environment variables
const CONTROL_PANEL_DOMAIN_URL = import.meta.env.ZUDOKU_PUBLIC_CONTROL_PANEL_DOMAIN_URL || "https://controlpanel.ogstaging.us";

/**
 * Clears the control panel session by loading logout URL in a hidden iframe.
 * Iframe approach avoids CORS issues and automatically sends cookies.
 */
/**
 * Loads a URL in a hidden iframe and resolves when loaded or timed out.
 */
export function loadUrlInIframe(url: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.setAttribute("aria-hidden", "true");

      const cleanup = () => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };

      iframe.onload = () => {
        console.log(`Iframe loaded: ${url}`);
        cleanup();
        resolve();
      };

      iframe.onerror = () => {
        console.warn(`Iframe failed to load: ${url}`);
        cleanup();
        resolve();
      };

      setTimeout(() => {
        console.warn(`Iframe timed out: ${url}`);
        cleanup();
        resolve();
      }, timeoutMs);

      iframe.src = url;
      document.body.appendChild(iframe);
    } catch (error) {
      console.warn(`Iframe error for ${url}:`, error);
      resolve();
    }
  });
}

export async function performLogout(): Promise<void> {
  const TIMEOUT_MS = 6000;
  const accessLogoutUrl = `${CONTROL_PANEL_DOMAIN_URL}/access/logout`;
  const auth0LogoutUrl = `${CONTROL_PANEL_DOMAIN_URL}/auth/users/logout_with_auth0?return_to=${encodeURIComponent(accessLogoutUrl)}&reset_cookie=true`;

  try {
    // 1. First call logout_with_auth0 to clear Auth0 session on control panel
    console.log("Starting control panel Auth0 logout...");
    await loadUrlInIframe(accessLogoutUrl, TIMEOUT_MS);

    // 2. Then call /access/logout to clear control panel session
    console.log("Starting control panel access logout...");
    await loadUrlInIframe(auth0LogoutUrl, TIMEOUT_MS);

    console.log("Control panel logout completed");
  } catch (error) {
    console.warn("Control panel logout failed:", error);
  }
}

// No needed as the control panel logout handles both delphi cookie and auth0 cookie invalidation
// /**
//  * Redirects to Auth0 logout endpoint to clear the Auth0 session.
//  * @see https://auth0.com/docs/authenticate/login/logout/log-users-out-of-auth0
//  */
// function performAuth0Logout(): void {
//   const returnUrl = new URL(window.location.origin);
//   returnUrl.pathname = "/";

//   const logoutUrl = new URL(`https://${AUTH0_DOMAIN}/v2/logout`);
//   logoutUrl.searchParams.set("client_id", AUTH0_CLIENT_ID);
//   logoutUrl.searchParams.set("returnTo", returnUrl.toString());

//   window.location.href = logoutUrl.toString();
// }

/**
 * Custom logout button that handles complete logout flow.
 * Clears Zudoku auth state, control panel session, and Auth0 session.
 * If rendered on /logout path, automatically triggers logout.
 */
export function LogoutButton() {
  console.log("LogoutButton code called");

  const { isAuthenticated, isAuthEnabled, setLoggedOut } = useAuth();
  const isLoggingOut = useRef(false);

  const handleLogout = async () => {
    // Prevent duplicate logout calls
    if (isLoggingOut.current) {
      console.log("Logout already in progress, skipping...");
      return;
    }
    isLoggingOut.current = true;

    try {
      // 1. Clear local auth state
      setLoggedOut();

      // 2. Clear control panel session
      await performLogout();

      window.location.href = window.location.origin + "/";
      console.log("Logout handled successfully");
    } catch (error) {
      console.error("Logout failed:", error);
      isLoggingOut.current = false; // Reset on error to allow retry
    }
  };

  // Auto-trigger logout if on /logout path
  useEffect(() => {
    if (window.location.pathname === "/logout" && isAuthEnabled && isAuthenticated) {
      handleLogout();
    }
  }, [isAuthEnabled, isAuthenticated]);

  // Don't render if auth is not enabled or user is not authenticated
  if (!isAuthEnabled || !isAuthenticated) {
    return null;
  }

  return null;

}

export default LogoutButton;
